import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, User as UserIcon } from "lucide-react";

/**
 * Global banner shown whenever a Super Admin has impersonated another user.
 * Poll-free after mount: checks /api/superadmin/impersonate/current once and stays
 * mounted until the user clicks "Torna al Super Admin".
 */
export default function ImpersonationBanner() {
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, status } = await api.get("/superadmin/impersonate/current");
        if (!cancelled && status === 200 && data?.session_id) {
          setSession(data);
        }
      } catch { /* not impersonating */ }
    })();
    return () => { cancelled = true; };
  }, []);

  async function exit() {
    if (!session) return;
    setBusy(true);
    try {
      await api.post("/superadmin/impersonate/exit", { session_id: session.session_id });
      toast.success("Sei tornato al Super Admin");
      setSession(null);
      nav("/superadmin", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }

  if (!session) return null;

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[60] w-full bg-gradient-to-r from-rose-600 via-rose-500 to-amber-500 text-slate-950 shadow-[0_4px_16px_rgba(244,63,94,0.35)]"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3 text-sm font-bold">
        <div className="inline-flex items-center gap-2 shrink-0">
          <Shield size={16} strokeWidth={2.5}/>
          <span className="uppercase text-[10px] tracking-[0.3em]">Impersonate attivo</span>
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2 truncate">
          <UserIcon size={14} className="shrink-0"/>
          <span className="truncate">
            Stai visualizzando come <span className="underline underline-offset-2">{session.target_name || session.target_email}</span>
          </span>
        </div>
        <button
          onClick={exit}
          disabled={busy}
          data-testid="impersonation-exit-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-950 text-white hover:bg-slate-800 text-xs uppercase tracking-widest font-black disabled:opacity-50 transition-colors"
        >
          <LogOut size={12}/> {busy ? "Uscita…" : "Torna al Super Admin"}
        </button>
      </div>
    </div>
  );
}
